import { useCallback, useEffect, useRef, useState } from "react";
import {
  JOB_TYPE_LABELS,
  type JobType,
  type Customer,
  type Property,
  type DumpsterSize,
  type Landfill,
  type Job,
} from "@eagledispatch/shared";
import { apiFetch, ApiError } from "../../lib/api";
import { idempotentCreate, invalidateOnEdit } from "../../lib/idempotent-create";
import Button from "../../components/Button";
import Card from "../../components/Card";
import FieldError from "../../components/FieldError";
import type { OfficeRoute } from "./OfficeHome";

type DropdownState<T> =
  | { status: "loading" }
  | { status: "ok"; data: T[] }
  | { status: "error" };

/**
 * Office JobForm — Unit #19.
 *
 * Focused form (no driver/truck fields) that creates an UNASSIGNED job.
 * The job lands as NEW with driver_id + truck_id NULL; the dispatcher
 * then assigns via the existing /jobs/:id/assign endpoint.
 *
 * Why a focused form vs. a `mode` prop on the dispatcher JobForm:
 *   - the dispatcher form has 2 large dropdowns (drivers, trucks) +
 *     the is_available filter + the cascade logic that the office form
 *     doesn't need at all;
 *   - a `mode` prop would force the dispatcher form to conditionally
 *     render two large sections, adding branchy state to a form
 *     that the dispatcher side already exercises under load;
 *   - 80 lines of focused form vs 300+ lines with conditional
 *     branches is the simpler shape. Both forms share the
 *     `useDropdown` pattern (copy-pasted, ~20 lines) — extracting a
 *     shared hook for two callers is overkill.
 *
 * Server rules (no client-side enforcement beyond UI shape):
 *   - POST /jobs OFFICE path requires driverId/truckId ABSENT → 400 if
 *     present. The form never sends them, so the field-error path is
 *     dead code (the server is the authority).
 *   - Job lands as NEW; the office doesn't see assignment until
 *     dispatch picks it up.
 */
export default function OfficeJobForm({
  onNavigate,
  onCreated,
  onToast,
}: {
  onNavigate: (route: OfficeRoute) => void;
  onCreated: (job: Job) => void;
  onToast?: (msg: string, kind?: "success" | "error") => void;
}) {
  const [customerId, setCustomerId] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [sizeId, setSizeId] = useState("");
  const [jobType, setJobType] = useState<JobType | "">("");
  const [landfillId, setLandfillId] = useState("");
  const [message, setMessage] = useState("");

  const [properties, setProperties] = useState<DropdownState<Property>>({
    status: "loading",
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // SPEC-48 Fix F: one idempotency key per logical submission, replayed on a
  // retry so a lost 201 cannot create a second job. See dispatcher JobForm for
  // the full contract; both forms post the same endpoint.
  const createKeyRef = useRef<string | null>(null);

  // DO-LANDFILL-OPTIONAL: a D/O (delivery-only) job has NO landfill — the
  // field is meaningless (nothing is dumped), so it is hidden and excluded
  // from the payload. The server independently enforces the same rule
  // (shared NewJob.superRefine rejects a D/O body that carries a landfillId),
  // so a stale client cannot smuggle one in.
  const isDeliveryOnly = jobType === "D_O";

  const [customers, reloadCustomers] = useDropdown<Customer>(() =>
    apiFetch<Customer[]>("/api/v1/customers"),
  );
  const [sizes, reloadSizes] = useDropdown<DumpsterSize>(() =>
    apiFetch<DumpsterSize[]>("/api/v1/dumpster-sizes"),
  );
  const [landfills, reloadLandfills] = useDropdown<Landfill>(() =>
    apiFetch<Landfill[]>("/api/v1/landfills"),
  );

  // Property cascade — same discipline as the dispatcher form
  // (AbortController + identity guard against stale responses).
  const propertiesAbortRef = useRef<AbortController | null>(null);
  const selectedCustomerRef = useRef(customerId);
  selectedCustomerRef.current = customerId;

  const loadProperties = useCallback((cid: string) => {
    propertiesAbortRef.current?.abort();
    const controller = new AbortController();
    propertiesAbortRef.current = controller;
    setProperties({ status: "loading" });
    apiFetch<Property[]>(`/api/v1/customers/${cid}/properties`, {
      signal: controller.signal,
    })
      .then((data) => {
        if (!controller.signal.aborted && selectedCustomerRef.current === cid) {
          setProperties({ status: "ok", data });
        }
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        if (selectedCustomerRef.current !== cid) return;
        setProperties({ status: "error" });
      });
  }, []);

  useEffect(() => {
    if (!customerId) {
      propertiesAbortRef.current?.abort();
      propertiesAbortRef.current = null;
      setProperties({ status: "loading" });
      setPropertyId("");
      return;
    }
    setPropertyId("");
    loadProperties(customerId);
    return () => propertiesAbortRef.current?.abort();
  }, [customerId, loadProperties]);

  const requiredReady =
    !!customerId &&
    !!propertyId &&
    !!sizeId &&
    !!jobType &&
    // DO-LANDFILL-OPTIONAL: D/O needs no landfill; other types still do.
    (isDeliveryOnly || !!landfillId);

  // SPEC-48 Fix F invariant: any change to the submit payload clears the held
  // key — an edited submission is a NEW intent and must not replay a cached
  // 201. One effect over the values, rather than a call threaded through every
  // onChange, so a future field can't be forgotten.
  useEffect(() => {
    invalidateOnEdit(createKeyRef);
  }, [customerId, propertyId, sizeId, jobType, landfillId, message]);

  const submit = async () => {
    if (!requiredReady || !jobType) return;
    setSubmitting(true);
    setSubmitError(null);
    setFieldErrors({});
    // Office path: no driverId, no truckId. The server's OFFICE branch
    // returns 400 if either is present — dead code in the form, but the
    // server is the authority. The form is frozen while `submitting`, so
    // this payload cannot drift from the key being sent.
    const result = await idempotentCreate<Job>("/api/v1/jobs", {
      customerId,
      propertyId,
      dumpsterSizeId: sizeId,
      jobType,
      // DO-LANDFILL-OPTIONAL: D/O carries no landfill. Spread of the key onto
      // the object (vs a conditional expression) keeps the key fully ABSENT
      // for D/O and present-otherwise — the wire never carries
      // landfillId: undefined.
      ...(isDeliveryOnly ? {} : { landfillId }),
      message: message.trim() === "" ? undefined : message,
    }, createKeyRef);
    setSubmitting(false);

    if (result.ok) {
      if (onToast) {
        onToast(`Job ${result.data.jobNumber} created — unassigned`);
      }
      onCreated(result.data);
      return;
    }
    const e = result.error;
    if (e instanceof ApiError) {
      if (Object.keys(e.fieldErrors).length > 0) {
        setFieldErrors(e.fieldErrors);
      } else {
        setSubmitError(e.message);
      }
    } else {
      setSubmitError(e instanceof Error ? e.message : String(e));
    }
  };

  function option(value: string, label: string) {
    return (
      <option key={value} value={value}>
        {label}
      </option>
    );
  }

  const JOB_TYPES = Object.keys(JOB_TYPE_LABELS) as JobType[];

  return (
    <main className="surface">
      <header className="surface-header">
        <Button variant="ghost" onClick={() => onNavigate({ name: "home" })}>
          ← Back
        </Button>
        <h1>New Unassigned Job</h1>
      </header>

      <Card>
        <p className="muted" style={{ marginBottom: "16px" }}>
          Office creates UNASSIGNED jobs. The dispatcher will assign a
          driver and truck before the driver sees it.
        </p>

        <form
          className="job-form"
          onSubmit={(e) => {
            e.preventDefault();
            void submit();
          }}
        >
          <label className="field">
            <span className="field-label">Customer</span>
            <select
              value={customerId}
              disabled={submitting}
              onChange={(e) => setCustomerId(e.target.value)}
            >
              <option value="">Select customer…</option>
              {customers.status === "ok" &&
                customers.data.map((c) => option(c.id, c.companyName))}
            </select>
            {customers.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load customers.</span>{" "}
                <button type="button" className="link-button" onClick={reloadCustomers}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.customerId} />
          </label>

          <label className="field">
            <span className="field-label">Job Site</span>
            <select
              value={propertyId}
              disabled={submitting || !customerId || properties.status !== "ok"}
              onChange={(e) => setPropertyId(e.target.value)}
            >
              <option value="">
                {customerId ? "Select property…" : "Choose a customer first"}
              </option>
              {properties.status === "ok" &&
                properties.data.map((p) =>
                  option(p.id, `${p.siteName} — ${p.addressLine1}`),
                )}
            </select>
            {!customerId && (
              <span className="muted">Pick a customer to load properties.</span>
            )}
            {properties.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load properties.</span>{" "}
                <button
                  type="button"
                  className="link-button"
                  onClick={() => loadProperties(customerId)}
                >
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.propertyId} />
          </label>

          <label className="field">
            <span className="field-label">Dumpster Size</span>
            <select
              value={sizeId}
              disabled={submitting}
              onChange={(e) => setSizeId(e.target.value)}
            >
              <option value="">Select size…</option>
              {sizes.status === "ok" &&
                sizes.data.map((s) => option(s.id, `${s.label} (${s.yards} yd)`))}
            </select>
            {sizes.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load sizes.</span>{" "}
                <button type="button" className="link-button" onClick={reloadSizes}>
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.dumpsterSizeId} />
          </label>

          <label className="field">
            <span className="field-label">Job Type</span>
            <select
              value={jobType}
              disabled={submitting}
              onChange={(e) => setJobType(e.target.value as JobType | "")}
            >
              <option value="">Select type…</option>
              {JOB_TYPES.map((t) => option(t, JOB_TYPE_LABELS[t]))}
            </select>
            <FieldError message={fieldErrors.jobType} />
          </label>

          {/* DO-LANDFILL-OPTIONAL: hidden for D/O — delivery-only means no
              landfill to pick. Switching the type away from D/O re-shows the
              dropdown and the gate re-demands a landfill. */}
          {isDeliveryOnly && (
            <p className="muted" style={{ fontSize: "13px" }}>
              Delivery-only — no landfill needed.
            </p>
          )}
          {!isDeliveryOnly && (
          <label className="field">
            <span className="field-label">Landfill</span>
            <select
              value={landfillId}
              disabled={submitting}
              onChange={(e) => setLandfillId(e.target.value)}
            >
              <option value="">Select landfill…</option>
              {landfills.status === "ok" &&
                landfills.data.map((l) => option(l.id, l.name))}
            </select>
            {landfills.status === "error" && (
              <span className="dropdown-error">
                <span className="err">Failed to load landfills.</span>{" "}
                <button
                  type="button"
                  className="link-button"
                  onClick={reloadLandfills}
                >
                  Retry
                </button>
              </span>
            )}
            <FieldError message={fieldErrors.landfillId} />
          </label>
          )}

          <label className="field">
            <span className="field-label">Message (optional)</span>
            <textarea
              value={message}
              readOnly={submitting}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Gate codes, contact name/phone, instructions…"
              rows={3}
            />
          </label>

          {submitError && <p className="form-error err">{submitError}</p>}

          <Button
            type="submit"
            variant="primary"
            className="send-button"
            disabled={!requiredReady || submitting}
          >
            {submitting ? "Creating…" : "Create Unassigned Job"}
          </Button>
        </form>
      </Card>
    </main>
  );
}

// Local useDropdown — duplicated from JobForm.tsx to keep the
// focused-form split clean. (Both forms follow the same pattern; a
// shared hook would be extracted when a third caller appears.)
function useDropdown<T>(load: () => Promise<T[]>): [DropdownState<T>, () => void] {
  const [state, setState] = useState<DropdownState<T>>({ status: "loading" });
  const loadRef = useRef(load);
  loadRef.current = load;

  const run = useCallback(() => {
    setState({ status: "loading" });
    loadRef
      .current()
      .then((data) => setState({ status: "ok", data }))
      .catch(() => setState({ status: "error" }));
  }, []);

  useEffect(() => {
    void run();
  }, [run]);
  return [state, run];
}
